import React, { useEffect, useState } from "react";
import { Alert, Box } from "@mui/material";
import useSystemHook from "../../hooks/SystemHook";
import {
  STEPS,
  STEP1_FIELDS,
  INITIAL_FORM,
  INITIAL_FILES,
  APPLICANT_TYPE,
  isDev,
} from "./constants";
import { ApplicationDocumentSlot } from "../../api/tradeApplicationController";
import {
  startApplication,
  saveApplicationDraft,
  uploadApplicationDocument,
  submitApplication,
} from "../../main-component/PageFunctions/tradeApplicationFunctions";
import { updateProfileData } from "../../main-component/PageFunctions/userFunctions";
import { useCaptcha } from "../Captcha";
import {
  dataUrlToFile,
  getNameParts,
  makeCallbacks,
  noop,
  splitContactName,
} from "../../utils/helpers";
import ApplicationLayout from "./ApplicationLayout";
import ApplicationSidebar from "./ApplicationSidebar";
import StepNavigation, { NextButton } from "./StepNavigation";
import AccountApplicationStep from "./AccountApplicationStep";
import DocumentsUploadStep from "./DocumentsUploadStep";
import DeedOfSuretyshipStep from "./DeedOfSuretyshipStep";
import TermsAndConditionsStep from "./TermsAndConditionsStep";
import ConfirmationStep from "./ConfirmationStep";

const CONFIRMATION_STEP = STEPS.length + 1;

const TradeApplication = ({ genericPageContent }) => {
  const { isMobile } = useSystemHook();
  const captcha = useCaptcha();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [idType, setIdType] = useState("companyRegNo");
  const [fileData, setFileData] = useState(INITIAL_FILES);
  const [agreedDeed, setAgreedDeed] = useState(false);
  const [sigSaved, setSigSaved] = useState(null);
  const [sureties, setSureties] = useState([
    { name: "", idNo: "", sig: null },
    { name: "", idNo: "", sig: null },
  ]);
  const [regNo, setRegNo] = useState("");
  const [tcAgreed, setTcAgreed] = useState(false);
  const [reference, setReference] = useState(null);
  const [resumeToken, setResumeToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSuretyNameChange = (i, value) =>
    setSureties((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, name: value } : s)),
    );

  const handleSuretyIdChange = (i, value) =>
    setSureties((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, idNo: value } : s)),
    );

  const handleSuretySave = (i, dataUrl) =>
    setSureties((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, sig: dataUrl } : s)),
    );

  const handleSuretyClear = (i) =>
    setSureties((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, sig: null } : s)),
    );

  const handleChange = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  // Pre-fill the email field from the account's own record (PageWrapper already fetches this GenericPageContent
  // for every page) so an applicant who registered via Auth0 doesn't retype it, then lock the field so the
  // submitted application stays tied to that account's email.
  useEffect(() => {
    if (genericPageContent?.email) handleChange("email", genericPageContent.email);
  }, [genericPageContent?.email]);

  const emailLocked = !!genericPageContent?.email;

  const handleFiles = (key, files) =>
    setFileData((prev) => ({ ...prev, [key]: files }));

  const buildAccountRequest = () => ({
    applicantType:
      idType === "idNo" ? APPLICANT_TYPE.individual : APPLICANT_TYPE.company,
    companyName: formData.companyName,
    companyType: formData.companyType,
    deliveryAddress: formData.deliveryAddress,
    deliveryLatitude: formData.deliveryAddressCoordinates?.lat ?? null,
    deliveryLongitude: formData.deliveryAddressCoordinates?.lng ?? null,
    routingInstructions: formData.routingInstructions || null,
    contactName: formData.contactName,
    email: formData.email,
    phoneNumber: formData.phone,
    vatNumber: idType === "companyRegNo" ? formData.vatNumber || null : null,
    companyRegNo: idType === "companyRegNo" ? formData.companyRegNo : null,
    idNumber: idType === "idNo" ? formData.idNo : null,
  });

  const buildDraftRequest = (extra = {}) => ({
    ...buildAccountRequest(),
    companyRegNo:
      regNo || (idType === "companyRegNo" ? formData.companyRegNo : null),
    directors: sureties
      .filter((s) => s.name?.trim() || s.idNo?.trim())
      .map((s, i) => ({ key: `d${i + 1}`, name: s.name, idNumber: s.idNo })),
    ...extra,
  });

  const activeIdValue =
    idType === "companyRegNo" ? formData.companyRegNo : formData.idNo;
  const hasFullName = getNameParts(formData.contactName).length >= 2;
  const isStep1Valid =
    STEP1_FIELDS.filter((f) => f.required).every(
      (f) => formData[f.key]?.trim().length > 0,
    ) &&
    activeIdValue.trim().length > 0 &&
    hasFullName &&
    captcha.isVerified;

  const isStep2Valid =
    fileData.directorFiles.length > 0 &&
    (idType === "idNo" || fileData.idVatFiles.length > 0);

  const hasOneSurety = sureties.some(
    (s) => !!s.sig && s.idNo.trim().length > 0 && s.name.trim().length > 0,
  );
  const isStep3Valid =
    hasOneSurety && regNo.trim().length > 0 && agreedDeed && !!sigSaved;

  const isStep4Valid = tcAgreed;

  const isCurrentStepValid =
    currentStep === 1
      ? isStep1Valid
      : currentStep === 2
        ? isStep2Valid
        : currentStep === 3
          ? isStep3Valid
          : currentStep === 4
            ? isStep4Valid
            : false;

  const handleNext = async () => {
    if (!isDev && !isCurrentStepValid) return;
    setSubmitError(null);

    if (currentStep === 1) {
      setIsSubmitting(true);
      let appRef = null;
      let appToken = null;
      await startApplication({
        request: buildAccountRequest(),
        captchaToken: captcha.token,
        setApplication: (data) => {
          appRef = data?.referenceNumber;
          appToken = data?.resumeToken;
        },
        ...makeCallbacks((msg) => setSubmitError(msg)),
      });
      setIsSubmitting(false);
      if (!appRef) return;
      setReference(appRef);
      setResumeToken(appToken);
      captcha.reset();
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setIsSubmitting(true);
      let failed = false;
      const onError = (msg) => {
        setSubmitError(msg);
        failed = true;
      };

      // Only upload VAT here (no directorKey needed).
      // Director ID files are uploaded in step 3 after directors are registered via saveApplicationDraft.
      if (!failed && fileData.idVatFiles.length > 0) {
        await uploadApplicationDocument({
          reference,
          resumeToken,
          slot: ApplicationDocumentSlot.VatRegistration,
          file: fileData.idVatFiles[0],
          ...makeCallbacks(onError),
        });
      }

      setIsSubmitting(false);
      if (!failed) setCurrentStep(3);
    } else if (currentStep === 3) {
      setIsSubmitting(true);
      let failed = false;
      const onError = (msg) => {
        setSubmitError(msg);
        failed = true;
      };

      // Save draft first — this registers the director keys (d1, d2…) on the backend.
      // Director ID files can only be assigned after their keys exist.
      await saveApplicationDraft({
        reference,
        resumeToken,
        request: buildDraftRequest(),
        ...makeCallbacks(onError),
      });

      // Director ID files (slot 3) — director keys now exist
      for (let i = 0; i < fileData.directorFiles.length && !failed; i++) {
        await uploadApplicationDocument({
          reference,
          resumeToken,
          slot: ApplicationDocumentSlot.DirectorIdCopy,
          directorKey: `d${i + 1}`,
          file: fileData.directorFiles[i],
          ...makeCallbacks(onError),
        });
      }

      if (!failed && sigSaved) {
        await uploadApplicationDocument({
          reference,
          resumeToken,
          slot: ApplicationDocumentSlot.DeedOfSuretySignature,
          file: dataUrlToFile(sigSaved, "deed-signature.png"),
          ...makeCallbacks(onError),
        });
      }

      for (let i = 0; i < sureties.length && !failed; i++) {
        if (sureties[i].sig) {
          await uploadApplicationDocument({
            reference,
            resumeToken,
            slot: ApplicationDocumentSlot.DirectorSuretySignature,
            directorKey: `d${i + 1}`,
            file: dataUrlToFile(sureties[i].sig, `surety-sig-d${i + 1}.png`),
            ...makeCallbacks(onError),
          });
        }
      }

      setIsSubmitting(false);
      if (!failed) setCurrentStep(4);
    } else if (currentStep === 4) {
      setIsSubmitting(true);
      let failed = false;
      const onError = (msg) => {
        setSubmitError(msg);
        failed = true;
      };

      await saveApplicationDraft({
        reference,
        resumeToken,
        request: buildDraftRequest({ acceptTermsOfSale: true }),
        ...makeCallbacks(onError),
      });

      if (!failed) {
        await submitApplication({
          reference,
          resumeToken,
          ...makeCallbacks(onError),
        });
      }

      // Same request ArgusRoleModal's Trade flow sends (update-profile with wholesaleTrader), mapped from the
      // data already collected here, so the applicant doesn't have to fill it in again for a single-step
      // application: the admin sees both the submitted application and the trade account request.
      if (!failed) {
        const { firstName, lastName } = splitContactName(formData.contactName);
        let profileErrorMessage = null;

        await updateProfileData({
          firstName,
          lastName,
          mobile: formData.phone,
          wholesaleTrader: true,
          personalIdNumber: formData.idNo,
          companyRegNo: regNo || formData.companyRegNo,
          personalCapacity: idType === "idNo",
          setResultMessage: (msg) => {
            profileErrorMessage = msg;
          },
          setStatusInProgress: noop,
          setStatusSuccess: noop,
          setStatusFailed: () =>
            onError(
              profileErrorMessage ||
                "Could not create the trade account request.",
            ),
          setStatusError: () =>
            onError(
              profileErrorMessage ||
                "Could not create the trade account request.",
            ),
        });
      }

      setIsSubmitting(false);
      if (!failed) setCurrentStep(CONFIRMATION_STEP);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  return (
    <ApplicationLayout isMobile={isMobile}>
      <ApplicationSidebar currentStep={currentStep} isMobile={isMobile} />

      <Box
        sx={{
          flex: 1,
          p: isMobile ? 3 : 5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <StepNavigation
          currentStep={currentStep}
          isCurrentStepValid={isCurrentStepValid && !isSubmitting}
          isMobile={isMobile}
          onBack={handleBack}
          onNext={handleNext}
        />

        {submitError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        {currentStep === 1 && (
          <AccountApplicationStep
            formData={formData}
            handleChange={handleChange}
            idType={idType}
            setIdType={setIdType}
            captchaBind={captcha.bind}
            emailLocked={emailLocked}
          />
        )}

        {currentStep === 2 && (
          <DocumentsUploadStep
            fileData={fileData}
            handleFiles={handleFiles}
            idType={idType}
          />
        )}

        {currentStep === 3 && (
          <DeedOfSuretyshipStep
            sureties={sureties}
            onSuretyNameChange={handleSuretyNameChange}
            onSuretyIdChange={handleSuretyIdChange}
            onSuretySave={handleSuretySave}
            onSuretyClear={handleSuretyClear}
            regNo={regNo}
            setRegNo={setRegNo}
            agreedDeed={agreedDeed}
            setAgreedDeed={setAgreedDeed}
            sigSaved={sigSaved}
            setSigSaved={setSigSaved}
            isMobile={isMobile}
          />
        )}

        {currentStep === 4 && (
          <TermsAndConditionsStep
            tcAgreed={tcAgreed}
            setTcAgreed={setTcAgreed}
            isMobile={isMobile}
          />
        )}

        {currentStep === CONFIRMATION_STEP && (
          <ConfirmationStep isMobile={isMobile} />
        )}

        {currentStep <= STEPS.length && (
          <NextButton
            currentStep={currentStep}
            isCurrentStepValid={isCurrentStepValid && !isSubmitting}
            isMobile={isMobile}
            onNext={handleNext}
          />
        )}
      </Box>
    </ApplicationLayout>
  );
};

export default TradeApplication;
